import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Image as ImageIcon, FileSpreadsheet } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { downloadCsv } from "../lib/quarters";

/* Employees First brand palette (Brand Identity Standards, 08.11.2021).
   Corporate blues #003A70 / #009ADE / #96DAEA; secondary data colours
   #8B9086, #C2D500, #FFC72C, #FF9E1B, #B39A80; type grey #54575A. */
export const BRAND = {
  darkBlue: "#003A70",
  blue: "#009ADE",
  lightBlue: "#96DAEA",
  coolGrey: "#8B9086",
  green: "#C2D500",
  yellow: "#FFC72C",
  orange: "#FF9E1B",
  tan: "#B39A80",
  typeGrey: "#54575A",
};

export const SERIES_COLORS = {
  premium: BRAND.darkBlue,
  fees: BRAND.coolGrey,
  claims: BRAND.orange,
  reserves: BRAND.darkBlue,
  surplus: BRAND.blue,
  claims_reserves: BRAND.orange,
  tech_fees: BRAND.darkBlue,
  carrier_fees: BRAND.coolGrey,
  management_fees: BRAND.tan,
  err: BRAND.darkBlue,
  accrued: BRAND.darkBlue,
  approved: BRAND.blue,
  paid: BRAND.lightBlue,
  available_cash: BRAND.green,
  ratio_ok: BRAND.darkBlue,
  ratio_over: BRAND.orange,
};

export const VENDOR_COLORS = [BRAND.darkBlue, BRAND.blue, BRAND.lightBlue, BRAND.yellow,
                              BRAND.orange, BRAND.green, BRAND.tan, BRAND.coolGrey];

export const ESTIMATE_COLORS = {
  premium: "#9FB8CE",
  fees: "#C3C7BF",
  claims: "#FFD39B",
  reserves: "#FFE6AC",
  surplus: "#BEE7F6",
  err: "#9FB8CE",
  ratio: "#A9BAC7",
};

export const CHART_GRID = "#ECE9DF";

export function ChartCard({ title, subtitle, note, rows, columns, baseName, testId, hasData = true, actions, caption, children }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const saveImage = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { backgroundColor: "#FFFFFF", pixelRatio: 2, skipFonts: true });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${baseName}-${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Chart image downloaded");
    } catch {
      toast.error("Could not capture the chart. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="border flex flex-col"
      style={{ borderColor: "var(--border)", background: "var(--surface)" }}
      data-testid={testId}
    >
      <div className="p-6 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="eyebrow">{subtitle}</div>
            <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>{title}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {actions}
            <button
              type="button"
              onClick={saveImage}
              disabled={busy || !hasData}
              className="btn-ghost flex items-center gap-1.5 text-xs"
              data-testid={`${testId}-image`}
            >
              <ImageIcon size={13} /> {busy ? "Saving…" : "Image"}
            </button>
            <button
              type="button"
              onClick={() => downloadCsv(`${baseName}.csv`, rows, columns)}
              disabled={!hasData}
              className="btn-ghost flex items-center gap-1.5 text-xs"
              data-testid={`${testId}-csv`}
            >
              <FileSpreadsheet size={13} /> CSV
            </button>
          </div>
        </div>
        {note && (
          <p className="mt-3 text-xs" style={{ color: "var(--muted)" }} data-testid={`${testId}-note`}>{note}</p>
        )}
      </div>
      <div ref={ref} id={`capture-${testId}`} data-chart-title={title} className="p-6 flex-1" style={{ background: "var(--surface)" }}>
        {hasData ? children : (
          <div className="h-56 flex items-center justify-center text-sm text-center px-6" style={{ color: "var(--muted)" }} data-testid={`${testId}-empty`}>
            No data for the selected range, product and company.
          </div>
        )}
        {hasData && caption && (
          <p className="mt-3 text-xs text-center" style={{ color: "var(--muted)" }} data-testid={`${testId}-range`}>{caption}</p>
        )}
      </div>
    </motion.div>
  );
}
